<template>
  <div class="mt-5">
    <div>
      <div class="calendar-title">
        <div class="columns is-vcentered">
          <div class="column is-two-fifths">{{ $t("projet.en-bref.calendrier") }}</div>
          <div class="column">
            <div class="container has-text-right">
              <a>
                <span v-if="!readonly && isPorteurProjet" @click="openEventModale = true">{{
                  $t("projet.en-bref.add-event")
                }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="is-flex is-horizontal-center">
        <b-datepicker v-model="selectedDate" inline :events="events"></b-datepicker>
      </div>
      <div class="event-container is-horizontal-center">
        <div
          v-for="evenement in selectedEvenements"
          :key="evenement.id"
          class="card mt-2 p-2 event"
        >
          <div class="card-content p0">
            <div class="event-content">
              {{ evenement.contenu }}
            </div>
            <div class="delete-event">
              <b-button
                v-if="!readonly && isPorteurProjet"
                size="is-small"
                type="is-danger"
                icon-right="trash"
                rounded
                @click="removeEvenement(evenement.projetAnnuel, evenement.id)"
              >
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <EventModale
      :projet-annuel="projetAnnuel"
      :open="openEventModale"
      :is-porteur-projet="isPorteurProjet"
      :unselectable-dates="unselectableDates"
      @close-modale="closeModale($event)"
    />
    <FileWithValidation
      v-if="calendriedFichier.id"
      :key="calendriedFichier.id"
      input-notice="Calendrier des activités"
      :file-operation-url="calendrierFichierUrl"
      :disabled="true"
      :model-value="true"
      default-file-name="calendrier.pdf"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { CalendrierEvenementDTO, FichierDTO, ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import EventModale from "./EventModale.vue"
import { EvenementService } from "@/services/rest/evenement/EvenementService"
import { FileService } from "@/services/rest/utils/FileService"
import FileWithValidation from "@/views/inputs/FileWithValidation.vue"
import { BDatepicker, BButton } from "buefy"

export default defineComponent({
  name: "CalendrierView",
  components: {
    EventModale,
    FileWithValidation,
    BButton,
    BDatepicker,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      evenementService: EvenementService.INSTANCE,
      fileService: FileService.INSTANCE,
      selectedDate: new Date(),
      selectedEvenements: [] as CalendrierEvenementDTO[],
      minDate: new Date(),
      maxDate: new Date(),
      events: [] as { date: Date; type: string }[],
      allEvenements: [] as CalendrierEvenementDTO[],
      unselectableDates: [] as Date[],
      openEventModale: false,
      calendriedFichier: new FichierDTO(),
      calendrierFichierUrl: "",
    }
  },
  watch: {
    selectedDate(): void {
      this.computeSelectedEvenements()
    },
  },
  mounted(): void {
    if (this.projetAnnuel && this.projetAnnuel.id) {
      this.minDate = new Date(this.projetAnnuel.anneeScolaireDebut)
      this.maxDate = new Date(this.projetAnnuel.anneeScolaireFin)
      this.loadEvenements(this.projetAnnuel.id, this.minDate, this.maxDate)
    }
  },
  methods: {
    async loadEvenements(idProjetAnnuel: string, minDate: Date, maxDate: Date): Promise<void> {
      this.allEvenements = await this.evenementService.getEvenements(
        idProjetAnnuel,
        minDate,
        maxDate,
      )
      this.events = this.buildEvends(this.allEvenements)
      this.computeSelectedEvenements()
      this.unselectableDates = this.buildUnselectableDates(this.allEvenements)
      const calendrierFichiers = await this.fileService.getFichiers(idProjetAnnuel, "CALENDRIER")
      if (calendrierFichiers && calendrierFichiers.length > 0) {
        this.calendriedFichier = calendrierFichiers[0]
        this.calendrierFichierUrl = this.fileService.buildUniqueFileURL(
          idProjetAnnuel,
          "CALENDRIER",
        )
      }
    },

    computeSelectedEvenements(): void {
      this.selectedEvenements = this.buildSelectedEvents(this.allEvenements, this.selectedDate)
    },

    buildEvends(evenements: CalendrierEvenementDTO[]): { date: Date; type: string }[] {
      return evenements.map((evenement) => ({
        date: new Date(evenement.dateEvenement),
        type: "is-danger",
      }))
    },

    buildSelectedEvents(
      evenements: CalendrierEvenementDTO[],
      date: Date,
    ): CalendrierEvenementDTO[] {
      const dateToCompare = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      return evenements.filter((evenement) => {
        const eventDate = new Date(evenement.dateEvenement)
        return (
          eventDate.getFullYear() === dateToCompare.getFullYear() &&
          eventDate.getMonth() === dateToCompare.getMonth() &&
          eventDate.getDate() === dateToCompare.getDate()
        )
      })
    },

    closeModale(withRefresh: boolean): void {
      this.openEventModale = false
      if (withRefresh && this.projetAnnuel && this.projetAnnuel.id) {
        this.loadEvenements(this.projetAnnuel.id, this.minDate, this.maxDate)
      }
    },

    buildUnselectableDates(evenements: CalendrierEvenementDTO[]): Date[] {
      const counterByDate = evenements.reduce((acc, curr) => {
        const counter = acc.get(curr.dateEvenement)
        return acc.set(curr.dateEvenement, counter ? counter + 1 : 1)
      }, new Map<Date | string, number>())
      const result: Date[] = []
      counterByDate.forEach((counter, date) => {
        if (counter > 2) {
          const newDate = new Date(date)
          newDate.setHours(0, 0, 0, 0)
          result.push(newDate)
        }
      })
      return result
    },

    removeEvenement(idProjetAnnuel: string, idEvenement: string): void {
      this.$buefy.dialog.confirm({
        message: this.$t("projet.en-bref.remove-event-confirm").toString(),
        confirmText: this.$t("confirmer").toString(),
        cancelText: this.$t("annuler").toString(),
        onConfirm: () =>
          this.evenementService
            .removeEvenement(idProjetAnnuel, idEvenement)
            .then(() => this.loadEvenements(idProjetAnnuel, this.minDate, this.maxDate)),
      })
    },
  },
})
</script>

<style lang="scss">
.calendar-title {
  color: #363636;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 15px;
}

.is-horizontal-center {
  justify-content: center;
}

.event-container {
  width: 100%;
}

.event {
  width: 100%;
}

.event-content {
  word-break: break-all;
  padding-right: 30px;
}

.delete-event {
  position: absolute;
  right: 15px;
  top: 15px;
}
</style>
